// src/components/ReposicionTabacoModal.js
//
// Reposición de cigarrillos · UX basada en el conteo de cigarrillos de CajaModal.
// - Cada apertura inicia una reposición NUEVA y vacía.
// - Escáner activo por defecto (SKU / código de barras / ID si el endpoint lo resuelve).
// - Búsqueda manual como alternativa.
// - Si un producto ya existe, la nueva cantidad REEMPLAZA la anterior; no se suma.
// - La tabla contiene solamente los productos incluidos en la reposición actual.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircleOutline as CheckIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteIcon,
  QrCodeScanner as ScannerIcon,
  RestartAlt as ResetIcon,
  Save as SaveIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import api from '../../api/api';

function getProductBarcode(product) {
  return String(
    product?.codigo_barras ||
      product?.codigo_barra ||
      product?.codigoBarras ||
      product?.codigoBarra ||
      product?.barcode ||
      product?.bar_code ||
      product?.ean ||
      product?.upc ||
      ''
  ).trim();
}

function normalizeProductsResponse(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.productos)) return data.productos;
  if (Array.isArray(data?.data?.productos)) return data.data.productos;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

function cleanPositiveInteger(value) {
  return String(value ?? '').replace(/\D/g, '');
}

export default function ReposicionTabacoModal({
  open,
  cajaId,
  onConfirm,
  onCancel,
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  const [items, setItems] = useState([]); // [{ product, qty }]

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState('1');
  const [quantityDialogOpen, setQuantityDialogOpen] = useState(false);
  const [quantitySource, setQuantitySource] = useState('manual');

  const [scanEnabled, setScanEnabled] = useState(true);
  const [scanFeedback, setScanFeedback] = useState(null);
  const [loadingScan, setLoadingScan] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const scanBufferRef = useRef('');
  const scanIdleTimerRef = useRef(null);
  const scanCaptureRef = useRef(null);
  const quantityInputRef = useRef(null);
  const searchInputRef = useRef(null);

  const totalUnits = useMemo(
    () =>
      items.reduce((sum, item) => {
        const qty = parseInt(item?.qty, 10);
        return sum + (Number.isNaN(qty) ? 0 : Math.max(0, qty));
      }, 0),
    [items]
  );

  const resetScannerBuffer = useCallback(() => {
    scanBufferRef.current = '';
    if (scanIdleTimerRef.current) {
      window.clearTimeout(scanIdleTimerRef.current);
      scanIdleTimerRef.current = null;
    }
  }, []);

  const focusScanner = useCallback(() => {
    window.setTimeout(() => {
      scanCaptureRef.current?.focus?.({ preventScroll: true });
    }, 40);
  }, []);

  const resetSession = useCallback(() => {
    setSearchQuery('');
    setLoadingSearch(false);
    setSuggestions([]);
    setItems([]);
    setSelectedProduct(null);
    setQuantity('1');
    setQuantityDialogOpen(false);
    setQuantitySource('manual');
    setScanEnabled(true);
    setScanFeedback(null);
    setLoadingScan(false);
    resetScannerBuffer();
  }, [resetScannerBuffer]);

  // Cada vez que se abre el modal comienza una reposición completamente nueva.
  useEffect(() => {
    if (!open) {
      resetScannerBuffer();
      return;
    }

    resetSession();
    focusScanner();
  }, [open, resetSession, resetScannerBuffer, focusScanner]);

  useEffect(() => {
    return () => {
      resetScannerBuffer();
    };
  }, [resetScannerBuffer]);

  const openProductQuantity = useCallback(
    (product, source = 'manual') => {
      if (!product?.id) {
        setSnackbar({
          open: true,
          message: 'El producto seleccionado no tiene un ID válido.',
          severity: 'warning',
        });
        return;
      }

      const existing = items.find(
        (item) => String(item?.product?.id) === String(product.id)
      );

      setSuggestions([]);
      setSelectedProduct(product);
      setQuantity(existing ? String(existing.qty) : '1');
      setQuantitySource(source);
      setQuantityDialogOpen(true);
      setScanFeedback(null);
      resetScannerBuffer();

      window.setTimeout(() => {
        quantityInputRef.current?.focus?.();
        quantityInputRef.current?.select?.();
      }, 80);
    },
    [items, resetScannerBuffer]
  );

  const closeQuantityDialog = useCallback(() => {
    setQuantityDialogOpen(false);
    setSelectedProduct(null);
    setQuantity('1');
    setQuantitySource('manual');
    resetScannerBuffer();
    if (scanEnabled) focusScanner();
  }, [focusScanner, resetScannerBuffer, scanEnabled]);

  const saveProductQuantity = useCallback(() => {
    if (!selectedProduct?.id) return;

    const qty = parseInt(quantity, 10);
    if (!Number.isFinite(qty) || qty <= 0) {
      setSnackbar({
        open: true,
        message: 'Ingrese una cantidad mayor que cero.',
        severity: 'warning',
      });
      quantityInputRef.current?.focus?.();
      return;
    }

    const productId = String(selectedProduct.id);
    const alreadyExists = items.some(
      (item) => String(item?.product?.id) === productId
    );

    setItems((prev) => {
      const index = prev.findIndex(
        (item) => String(item?.product?.id) === productId
      );

      if (index === -1) {
        return [...prev, { product: selectedProduct, qty }];
      }

      // IMPORTANTE: reemplaza la cantidad; NO suma a la reposición anterior.
      return prev.map((item, itemIndex) =>
        itemIndex === index ? { ...item, product: selectedProduct, qty } : item
      );
    });

    setQuantityDialogOpen(false);
    setSelectedProduct(null);
    setQuantity('1');
    setSearchQuery('');
    setSuggestions([]);
    resetScannerBuffer();

    setScanFeedback({
      severity: 'success',
      message: `${selectedProduct?.nombre || `Producto #${productId}`}: ${qty} unidad(es) ${
        alreadyExists ? 'actualizadas' : 'agregadas'
      } a la reposición.`,
    });

    if (scanEnabled) focusScanner();
  }, [selectedProduct, quantity, items, resetScannerBuffer, scanEnabled, focusScanner]);

  const findExactProduct = useCallback((products, rawCode) => {
    const code = String(rawCode || '').trim();
    if (!code) return null;

    return (
      products.find((product) => {
        const id = String(product?.id || '').trim();
        const sku = String(product?.sku || product?.codigo || '').trim();
        const barcode = getProductBarcode(product);
        return id === code || sku === code || barcode === code;
      }) || null
    );
  }, []);

  const lookupScannedProduct = useCallback(
    async (rawCode) => {
      const code = String(rawCode || '').trim();
      if (!code || loadingScan) return;

      setLoadingScan(true);
      setScanFeedback(null);

      try {
        const { data } = await api.get('/inventario_productos', {
          params: { search: code, limit: 20, page: 1 },
        });

        const products = normalizeProductsResponse(data);
        const exact = findExactProduct(products, code);
        const product = exact || (products.length === 1 ? products[0] : null);

        if (!product) {
          setScanFeedback({
            severity: 'warning',
            message:
              products.length > 1
                ? `El código ${code} devolvió varios productos. Utilice la búsqueda manual para seleccionar el correcto.`
                : `Código ${code} no encontrado.`,
          });
          focusScanner();
          return;
        }

        openProductQuantity(product, 'scanner');
      } catch (error) {
        console.error('Error buscando producto para reposición:', error);
        setScanFeedback({
          severity: 'error',
          message: 'No fue posible consultar el producto escaneado.',
        });
        focusScanner();
      } finally {
        setLoadingScan(false);
      }
    },
    [findExactProduct, focusScanner, loadingScan, openProductQuantity]
  );

  // Captura del lector de códigos. Se ignoran teclas mientras el usuario escribe
  // en un input/textarea y mientras está abierto el diálogo de cantidad.
  useEffect(() => {
    if (!open || !scanEnabled || quantityDialogOpen || loadingScan) return undefined;

    const clearIdleTimer = () => {
      if (scanIdleTimerRef.current) {
        window.clearTimeout(scanIdleTimerRef.current);
        scanIdleTimerRef.current = null;
      }
    };

    const armIdleTimer = () => {
      clearIdleTimer();
      scanIdleTimerRef.current = window.setTimeout(() => {
        scanBufferRef.current = '';
        scanIdleTimerRef.current = null;
      }, 5000);
    };

    const consumeEvent = (event) => {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();
    };

    const handler = (event) => {
      if (event.ctrlKey || event.altKey || event.metaKey) return;

      const target = event.target;
      const tag = String(target?.tagName || '').toLowerCase();
      const editing =
        tag === 'input' || tag === 'textarea' || target?.isContentEditable;
      if (editing) return;

      if (event.key === 'Enter') {
        consumeEvent(event);
        clearIdleTimer();

        const code = scanBufferRef.current.trim();
        scanBufferRef.current = '';

        if (code.length >= 3) {
          lookupScannedProduct(code);
        } else if (code.length > 0) {
          setScanFeedback({
            severity: 'warning',
            message: 'Código demasiado corto. Escanee el código completo.',
          });
          focusScanner();
        }
        return;
      }

      if (event.key === 'Escape') {
        if (scanBufferRef.current) {
          consumeEvent(event);
          clearIdleTimer();
          scanBufferRef.current = '';
          focusScanner();
        }
        return;
      }

      if (event.key === 'Backspace' && scanBufferRef.current) {
        consumeEvent(event);
        scanBufferRef.current = scanBufferRef.current.slice(0, -1);
        armIdleTimer();
        return;
      }

      if (event.key.length === 1) {
        consumeEvent(event);
        scanBufferRef.current += event.key;
        armIdleTimer();
      }
    };

    focusScanner();
    window.addEventListener('keydown', handler, true);

    return () => {
      window.removeEventListener('keydown', handler, true);
      clearIdleTimer();
    };
  }, [
    open,
    scanEnabled,
    quantityDialogOpen,
    loadingScan,
    lookupScannedProduct,
    focusScanner,
  ]);

  const handleSearch = async (event) => {
    event?.preventDefault?.();

    const query = String(searchQuery || '').trim();
    if (!query) {
      setSnackbar({
        open: true,
        message: 'Ingrese un término de búsqueda.',
        severity: 'warning',
      });
      searchInputRef.current?.focus?.();
      return;
    }

    setLoadingSearch(true);
    setSuggestions([]);
    setScanFeedback(null);

    try {
      const { data } = await api.get('/inventario_productos', {
        params: { search: query, limit: 20, page: 1 },
      });

      const products = normalizeProductsResponse(data);
      const exact = findExactProduct(products, query);

      if (exact) {
        openProductQuantity(exact, 'manual');
        return;
      }

      if (products.length === 1) {
        openProductQuantity(products[0], 'manual');
        return;
      }

      setSuggestions(products);

      if (!products.length) {
        setScanFeedback({
          severity: 'info',
          message: `No se encontraron productos para “${query}”.`,
        });
      }
    } catch (error) {
      console.error('Error buscando producto para reposición:', error);
      setSnackbar({
        open: true,
        message: 'No fue posible realizar la búsqueda.',
        severity: 'error',
      });
    } finally {
      setLoadingSearch(false);
    }
  };

  const handleRemoveItem = (productId) => {
    setItems((prev) =>
      prev.filter(
        (item) => String(item?.product?.id) !== String(productId)
      )
    );
    setScanFeedback({
      severity: 'info',
      message: 'Producto eliminado de esta reposición.',
    });
  };

  const handleReset = () => {
    if (!items.length) {
      setSearchQuery('');
      setSuggestions([]);
      setScanFeedback(null);
      resetScannerBuffer();
      focusScanner();
      return;
    }

    if (!window.confirm('¿Reiniciar la reposición de cigarrillos?')) return;

    setItems([]);
    setSearchQuery('');
    setSuggestions([]);
    setScanFeedback({ severity: 'info', message: 'Reposición reiniciada.' });
    resetScannerBuffer();
    focusScanner();
  };

  const handleCancel = () => {
    resetSession();
    onCancel?.();
  };

  const handleSend = () => {
    if (!items.length) {
      setSnackbar({
        open: true,
        message: 'No hay productos para reponer.',
        severity: 'warning',
      });
      return;
    }

    const invalidItem = items.find((item) => {
      const qty = parseInt(item?.qty, 10);
      return !Number.isFinite(qty) || qty <= 0;
    });

    if (invalidItem) {
      setSnackbar({
        open: true,
        message: `Revise la cantidad de ${
          invalidItem?.product?.nombre || 'uno de los productos'
        }.`,
        severity: 'warning',
      });
      return;
    }

    const detail = {};
    items.forEach(({ product, qty }) => {
      detail[String(product.id)] = parseInt(qty, 10);
    });

    // Limpiamos ANTES de delegar para evitar que la siguiente apertura herede datos,
    // incluso si el componente padre mantiene montado este modal.
    resetSession();
    onConfirm?.(detail);
  };

  const toggleScanner = () => {
    setScanEnabled((prev) => {
      const next = !prev;
      resetScannerBuffer();
      setScanFeedback({
        severity: 'info',
        message: next ? 'Escáner activado.' : 'Escáner pausado.',
      });
      if (next) focusScanner();
      return next;
    });
  };

  const selectedBarcode = getProductBarcode(selectedProduct || {});

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCancel}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 2.5 } }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={900}>
              Reposición de cigarrillos
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Escanee un producto o búsquelo manualmente e indique únicamente la cantidad que se repondrá.
              {cajaId ? ` · Caja #${cajaId}` : ''}
            </Typography>
          </Box>

          <IconButton onClick={handleCancel} aria-label="Cerrar reposición de cigarrillos">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent ref={scanCaptureRef} tabIndex={-1} sx={{ outline: 'none' }}>
          <Stack spacing={2}>
            {scanFeedback ? (
              <Alert
                severity={scanFeedback.severity || 'info'}
                onClose={() => setScanFeedback(null)}
              >
                {scanFeedback.message}
              </Alert>
            ) : null}

            <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ sm: 'center' }}
                justifyContent="space-between"
              >
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Chip
                    icon={loadingScan ? <CircularProgress size={16} /> : <ScannerIcon />}
                    color={scanEnabled ? 'success' : 'default'}
                    label={
                      loadingScan
                        ? 'Consultando código…'
                        : scanEnabled
                          ? 'Escáner activo'
                          : 'Escáner pausado'
                    }
                    variant={scanEnabled ? 'filled' : 'outlined'}
                  />

                  <Chip
                    size="small"
                    variant="outlined"
                    color={items.length ? 'primary' : 'default'}
                    label={`${items.length} producto${items.length === 1 ? '' : 's'}`}
                  />

                  <Chip
                    size="small"
                    variant="outlined"
                    color={totalUnits > 0 ? 'success' : 'default'}
                    label={`${totalUnits} unidad${totalUnits === 1 ? '' : 'es'}`}
                  />
                </Stack>

                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ScannerIcon />}
                    onClick={toggleScanner}
                    disabled={loadingScan}
                  >
                    {scanEnabled ? 'Pausar escáner' : 'Activar escáner'}
                  </Button>

                  <Button
                    size="small"
                    color="inherit"
                    startIcon={<ResetIcon />}
                    onClick={handleReset}
                  >
                    Reiniciar
                  </Button>
                </Stack>
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
                Búsqueda manual
              </Typography>

              <Box
                component="form"
                onSubmit={handleSearch}
                sx={{ display: 'flex', gap: 1 }}
              >
                <TextField
                  inputRef={searchInputRef}
                  value={searchQuery}
                  onChange={(event) => {
                    setSearchQuery(event.target.value);
                    setSuggestions([]);
                  }}
                  placeholder="Nombre, SKU o código de barras"
                  size="small"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  disabled={loadingSearch}
                  sx={{ minWidth: 48 }}
                  aria-label="Buscar producto"
                >
                  {loadingSearch ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <SearchIcon />
                  )}
                </Button>
              </Box>
            </Paper>

            {suggestions.length > 0 ? (
              <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ px: 1.5, pt: 1.25 }}>
                  <Typography variant="subtitle2" fontWeight={800}>
                    Resultados de búsqueda
                  </Typography>
                </Box>

                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Producto</TableCell>
                      <TableCell>SKU / Código</TableCell>
                      <TableCell align="right" sx={{ width: 120 }}>
                        Acción
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {suggestions.map((product) => {
                      const barcode = getProductBarcode(product);
                      const alreadyAdded = items.some(
                        (item) => String(item?.product?.id) === String(product?.id)
                      );

                      return (
                        <TableRow key={product.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={700}>
                              {product?.nombre || `Producto #${product.id}`}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{product?.sku || '—'}</Typography>
                            {barcode ? (
                              <Typography variant="caption" color="text.secondary">
                                {barcode}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant={alreadyAdded ? 'outlined' : 'contained'}
                              onClick={() => openProductQuantity(product, 'manual')}
                            >
                              {alreadyAdded ? 'Editar' : 'Agregar'}
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Paper>
            ) : null}

            <Box>
              <Typography variant="subtitle1" fontWeight={900}>
                Productos de esta reposición
              </Typography>
              <Typography variant="caption" color="text.secondary">
                La cantidad se registra únicamente en el diálogo que aparece al escanear o seleccionar un producto.
                Una nueva lectura permite reemplazar la cantidad anterior; no se suma automáticamente.
              </Typography>
            </Box>

            {!items.length ? (
              <Alert severity="info" icon={<ScannerIcon />}>
                Aún no hay productos en esta reposición. Escanee un cigarrillo o utilice la búsqueda manual.
              </Alert>
            ) : (
              <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Producto</TableCell>
                      <TableCell>SKU / Código</TableCell>
                      <TableCell align="center" sx={{ width: 110 }}>
                        Estado
                      </TableCell>
                      <TableCell align="right" sx={{ width: 170 }}>
                        Cantidad a reponer
                      </TableCell>
                      <TableCell align="center" sx={{ width: 70 }}>
                        Acción
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {items.map((item) => {
                      const product = item.product || {};
                      const barcode = getProductBarcode(product);
                      const parsedQty = parseInt(item.qty, 10);
                      const validQty = Number.isFinite(parsedQty) && parsedQty > 0;

                      return (
                        <TableRow
                          key={product.id}
                          sx={{ bgcolor: validQty ? 'action.hover' : undefined }}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight={700}>
                              {product?.nombre || `Producto #${product.id}`}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2">{product?.sku || '—'}</Typography>
                            {barcode ? (
                              <Typography variant="caption" color="text.secondary">
                                {barcode}
                              </Typography>
                            ) : null}
                          </TableCell>

                          <TableCell align="center">
                            <Chip
                              size="small"
                              icon={validQty ? <CheckIcon /> : undefined}
                              color={validQty ? 'success' : 'warning'}
                              label={validQty ? 'Agregado' : 'Revisar'}
                              variant="outlined"
                            />
                          </TableCell>

                          <TableCell align="right">
                            <Typography
                              variant="body1"
                              fontWeight={900}
                              color={validQty ? 'text.primary' : 'warning.main'}
                            >
                              {validQty ? parsedQty : '—'}
                            </Typography>
                          </TableCell>

                          <TableCell align="center">
                            <Tooltip title="Eliminar de esta reposición">
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() => handleRemoveItem(product.id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Paper>
            )}
          </Stack>
        </DialogContent>

        <Divider />

        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={handleCancel}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSend}
            disabled={!items.length}
            startIcon={<SaveIcon />}
          >
            Confirmar reposición
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={quantityDialogOpen}
        onClose={closeQuantityDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Registrar cantidad a reponer</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Box>
              <Typography fontWeight={900}>
                {selectedProduct?.nombre || 'Producto'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {[selectedProduct?.sku, selectedBarcode]
                  .filter(Boolean)
                  .join(' · ') || 'Sin código disponible'}
              </Typography>
            </Box>

            {quantitySource === 'scanner' ? (
              <Chip
                icon={<ScannerIcon />}
                label="Producto leído con escáner"
                color="success"
                variant="outlined"
                sx={{ alignSelf: 'flex-start' }}
              />
            ) : null}

            <TextField
              inputRef={quantityInputRef}
              label="Cantidad a reponer"
              value={quantity}
              onChange={(event) =>
                setQuantity(cleanPositiveInteger(event.target.value))
              }
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  event.stopPropagation();
                  saveProductQuantity();
                }
              }}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', min: 1 }}
              autoFocus
              fullWidth
            />

            {items.some(
              (item) => String(item?.product?.id) === String(selectedProduct?.id)
            ) ? (
              <Alert severity="info">
                Este producto ya está en la reposición. Guardar reemplazará su cantidad actual; no la sumará.
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeQuantityDialog}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={saveProductQuantity}
            startIcon={<SaveIcon />}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

ReposicionTabacoModal.propTypes = {
  open: PropTypes.bool.isRequired,
  cajaId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  onConfirm: PropTypes.func.isRequired, // recibe { productoId: cantidad }
  onCancel: PropTypes.func.isRequired,
};
